import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, DialogContent, CircularProgress } from '@mui/material';
import styles from './SelectMembersDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import SearchBar from '../atoms/SearchBar';
import MultiSelectList from '../composites/MultiSelectList';
import { getFormalDateString, getFullName } from '../../utils/dashboardUtils';
import { useSessionData } from '../../providers';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import type { Member } from '../../types';

export interface SelectMembersDialogProps {
  memberIds: string[];
  isOpen: boolean;
  onSubmit: (memberIds: string[]) => Promise<void> | void;
  onClose: () => void;
}

const SelectMembersDialog: React.FC<SelectMembersDialogProps> = ({
  onClose,
  onSubmit,
  memberIds,
  isOpen,
}) => {
  const { members, trainers } = useSessionData();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  const filteredMembers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return Object.values(members)
      .filter((member) => {
        if (!normalizedQuery) return true;
        return getFullName(member).toLowerCase().includes(normalizedQuery);
      })
      .filter((member) => memberIds.includes(member.id))
      .sort(
        (a, b) =>
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || -1,
      );
  }, [searchQuery, members, memberIds]);

  // Initialize selectedMembers with filteredMembers whenever filteredMembers changes
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);

  // Update selectedMembers when filteredMembers changes or when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedMembers(filteredMembers);
    }
  }, [filteredMembers, isOpen]);

  useEffect(() => {
    // Always reset submitting state when open state changes to avoid stale loaders
    setIsSubmitting(false);
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedMembers.map(({ id }) => id));
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={isOpen}
      title="Assign Program to All Group Members"
      actions={
        <Button
          className={styles.submit}
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} sx={{ color: 'var(--color-accent)' }} />
            ) : undefined
          }
        >
          <span className={styles.submitText}>
            {isSubmitting ? 'Assigning…' : 'Assign Program'}
          </span>
        </Button>
      }
    >
      <DialogContent className={styles.selectMembersDialog}>
        <SearchBar
          className={styles.search}
          placeholder="Search Members"
          setSearchQuery={setSearchQuery}
        />
        <MultiSelectList
          listClassName={styles.list}
          items={filteredMembers}
          selectedItems={selectedMembers}
          setSelectedItems={setSelectedMembers}
          EmptyStateComponent={() => null}
          ItemComponent={({ data }) => (
            <div className={styles.memberMultiSelectItem}>
              <div className={styles.memberNameWrapper}>
                <PersonOutlineOutlinedIcon color="primary" />
                <div className={styles.memberName}>{getFullName(data)}</div>
              </div>
              <div>
                {`${data.createdDate ? getFormalDateString(new Date(data.createdDate)) : ''}`}
              </div>
              <div>{getFullName(trainers[data.trainerIds[0]]?.trainer)}</div>
            </div>
          )}
        />
      </DialogContent>
    </BaseDialog>
  );
};

export default SelectMembersDialog;
